import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { animated, config, useSpring } from 'react-spring';
import { AnimatedBlock, snapTo, useScroll } from 'react-ui-animate';
import { useElementSize } from 'usehooks-ts';

import { useCustomer } from 'common/hooks';

import './AnimatedHeader.css';

const Collapsed = (isCollapsed: boolean): React.FC<React.PropsWithChildren> =>
  React.memo(({ children }) => {
    if (isCollapsed) return children;
    return null;
  });
const Extended = (isCollapsed: boolean): React.FC<React.PropsWithChildren> =>
  React.memo(({ children }) => {
    if (!isCollapsed) return children;
    return null;
  });

type InjectedProps = {
  isCollapsed: boolean;
  AnimatedHeaderElement: React.FC<React.PropsWithChildren>;
  AnimatedHeaderElementCollapsed: React.FC<React.PropsWithChildren>;
  Animated: typeof AnimatedBlock;
  loading?: boolean;
};

type Props = {
  children: (props: InjectedProps) => React.ReactNode;
  minHeight: number;
  classNameCollapsed?: string;
  className?: string;
  triggerPoint?: number;
  forceCollapsed?: boolean;
};

let allowCollapse = false;

export const AnimatedHeader: React.FC<Props> = React.memo(
  ({ children, minHeight, classNameCollapsed = '', className = '', triggerPoint = 10, forceCollapsed = false }) => {
    const {
      isAuthenticated,
      balance: { creditBalanceUsd },
    } = useCustomer();
    const headerHeight = !isAuthenticated || !creditBalanceUsd || creditBalanceUsd < 0 ? 257 : 281;
    const [initialHeight, storeInitialHeight] = useState(headerHeight);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [ref, { height: childrenHeight }] = useElementSize();

    const isCollapsed = useMemo(
      () => forceCollapsed || scrollPosition >= triggerPoint,
      [forceCollapsed, scrollPosition, triggerPoint],
    );

    useEffect(() => {
      if (!initialHeight && childrenHeight) storeInitialHeight(childrenHeight);
    }, [childrenHeight, initialHeight]);

    const [{ scroll }, set] = useSpring(() => ({
      scroll: 0,
      config: { ...config.stiff, bounce: 0 },
    }));

    useScroll(({ scrollY, velocityY }) => {
      if (forceCollapsed) return;

      if (!isCollapsed) {
        allowCollapse = document.documentElement.scrollHeight - window.screen.height >= initialHeight - minHeight;
      }
      const snapScrollValue = snapTo(scrollY, velocityY, [0, triggerPoint]);

      if (allowCollapse) {
        set({ scroll: snapScrollValue });
        setScrollPosition(snapScrollValue);
        return;
      }
      set({ scroll: 0 });
      setScrollPosition(0);
    });

    const AnimatedHeaderElementCollapsed = useMemo(() => Collapsed(isCollapsed), [isCollapsed]);
    const AnimatedHeaderElement = useMemo(() => Extended(isCollapsed), [isCollapsed]);

    const childProps = useMemo(
      () => ({
        AnimatedHeaderElementCollapsed,
        Animated: AnimatedBlock,
        AnimatedHeaderElement,
        initialHeight: childrenHeight,
        loading: !childrenHeight,
        isCollapsed,
      }),
      [AnimatedHeaderElement, AnimatedHeaderElementCollapsed, childrenHeight, isCollapsed],
    );

    const headerClassNames = classNames({
      header: true,
      'header-animated__animated': true,
      header__collapsed: isCollapsed,
      [className]: className,
      [classNameCollapsed]: classNameCollapsed,
    });

    if (initialHeight && !forceCollapsed) {
      const height = scroll.to([0, triggerPoint], [initialHeight, minHeight]);

      return (
        <>
          <animated.div
            style={{
              height,
              position: 'fixed',
              top: 0,
              boxSizing: 'border-box',
            }}
            className='header-animated'
          >
            <div className={headerClassNames} style={{ boxSizing: 'border-box' }}>
              {children({ ...childProps })}
            </div>
          </animated.div>
          <animated.div
            className='header-animated'
            style={{
              height,
              opacity: 0,
              position: 'initial',
              top: 0,
              zIndex: -1,
            }}
          >
            <div className={headerClassNames} style={{ boxSizing: 'border-box' }} />
          </animated.div>
        </>
      );
    }

    return (
      <div ref={ref} className='header-animated'>
        <div className={headerClassNames} style={{ boxSizing: 'border-box' }}>
          {children({ ...childProps })}
        </div>
      </div>
    );
  },
);
